import { useEffect, useState, useCallback } from 'react';
import { Bot, CheckCircle, Archive, FileText, Image, Video, Mail, Mic, BookOpen, RefreshCw, ExternalLink, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Asset, AssetType } from '../../types/assetRegistry';
import { AssetTypeBadge, BrandBadge } from '../../components/assetRegistry/Badges';

const AGENT_COLORS: Record<string, [string, string]> = {
  'athena-content':      ['#ede9fe', '#5b21b6'],
  'apollo-cba':          ['#dbeafe', '#1e40af'],
  'hermes-pipeline':     ['#fef3c7', '#92400e'],
  'sentinel-success':    ['#ccfbf1', '#115e59'],
  'oracle-intelligence': ['#ffe4e6', '#9f1239'],
};

function AgentBadge({ agentId }: { agentId: string | null }) {
  if (!agentId) return null;
  const [bg, color] = AGENT_COLORS[agentId] ?? ['#f3f4f6', '#374151'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: bg, color }}>
      <Bot size={10} />{agentId}
    </span>
  );
}

function AssetTypeIcon({ type }: { type: AssetType | null }) {
  const p = { size: 32, color: '#d1d5db' };
  switch (type) {
    case 'image': return <Image {...p} />;
    case 'video': return <Video {...p} />;
    case 'copy': return <FileText {...p} />;
    case 'email_template': return <Mail {...p} />;
    case 'podcast': return <Mic {...p} />;
    case 'training_module': return <BookOpen {...p} />;
    default: return <FileText {...p} />;
  }
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

function QueueCard({ asset, onApprove, onArchive, processing }: {
  asset: Asset; onApprove: (id: string) => void; onArchive: (id: string) => void; processing: boolean;
}) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'box-shadow 0.2s' }}>
      {/* Thumbnail */}
      <div style={{ position: 'relative', aspectRatio: '16/9', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {asset.thumbnail_url
          ? <img src={asset.thumbnail_url} alt={asset.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          : <AssetTypeIcon type={asset.asset_type} />}
        <div style={{ position: 'absolute', top: 8, left: 8 }}><AgentBadge agentId={asset.agent_id} /></div>
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 20 }}>
          <Clock size={10} />{timeAgo(asset.created_at)}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.4 }}>{asset.title}</p>
        {asset.description && <p style={{ margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{asset.description}</p>}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <AssetTypeBadge type={asset.asset_type} />
          <BrandBadge brand={asset.brand_entity} />
        </div>
        {asset.file_url && (
          <a href={asset.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1B4B8A', textDecoration: 'none' }}>
            View asset <ExternalLink size={10} />
          </a>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', borderTop: '1px solid #f3f4f6' }}>
        <button onClick={() => onArchive(asset.id)} disabled={processing}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', fontSize: 12, fontWeight: 600, color: '#6b7280', background: 'none', border: 'none', cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? 0.4 : 1, transition: 'background 0.15s' }}
          onMouseEnter={e => { if (!processing) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}>
          <Archive size={14} />Reject
        </button>
        <div style={{ width: 1, background: '#f3f4f6' }} />
        <button onClick={() => onApprove(asset.id)} disabled={processing}
          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 0', fontSize: 12, fontWeight: 600, color: '#059669', background: 'none', border: 'none', cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? 0.4 : 1, transition: 'background 0.15s' }}
          onMouseEnter={e => { if (!processing) (e.currentTarget as HTMLButtonElement).style.background = '#f0fdf4'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}>
          <CheckCircle size={14} />Approve
        </button>
      </div>
    </div>
  );
}

interface ReviewQueueProps { onCountChange?: (count: number) => void; }

export function ReviewQueue({ onCountChange }: ReviewQueueProps) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [filterAgent, setFilterAgent] = useState('');
  const [justActioned, setJustActioned] = useState<{ id: string; action: 'approved' | 'rejected' } | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('assets').select('*').eq('status', 'draft').eq('source', 'agent').order('created_at', { ascending: true });
    const results = (data ?? []) as Asset[];
    setAssets(results);
    onCountChange?.(results.length);
    setLoading(false);
  }, [onCountChange]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  async function handleApprove(id: string) {
    setProcessing(id);
    await supabase.from('assets').update({ status: 'approved' }).eq('id', id);
    setJustActioned({ id, action: 'approved' });
    setTimeout(() => {
      setAssets(prev => { const next = prev.filter(a => a.id !== id); onCountChange?.(next.length); return next; });
      setJustActioned(null); setProcessing(null);
    }, 600);
  }

  async function handleArchive(id: string) {
    setProcessing(id);
    await supabase.from('assets').update({ status: 'archived' }).eq('id', id);
    setJustActioned({ id, action: 'rejected' });
    setTimeout(() => {
      setAssets(prev => { const next = prev.filter(a => a.id !== id); onCountChange?.(next.length); return next; });
      setJustActioned(null); setProcessing(null);
    }, 600);
  }

  const agentIds = Array.from(new Set(assets.map(a => a.agent_id).filter(Boolean))) as string[];
  const filtered = filterAgent ? assets.filter(a => a.agent_id === filterAgent) : assets;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f7f9fb' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Bot size={22} color="#1B4B8A" />
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Wizkind Review Queue</h1>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: '#6b7280' }}>Assets created by agents — review before they enter the live registry</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {assets.length > 0 && (
              <span style={{ background: '#1B4B8A', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>{assets.length} pending</span>
            )}
            <button onClick={fetchQueue} disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 12, fontWeight: 600, color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: 8, background: '#fff', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>
              <RefreshCw size={13} style={{ animation: loading ? 'spin 0.7s linear infinite' : 'none' }} />Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Agent filter */}
      {agentIds.length > 1 && (
        <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Filter by agent:</span>
          {[{ id: '', label: `All (${assets.length})` }, ...agentIds.map(id => ({ id, label: `${id} (${assets.filter(a => a.agent_id === id).length})` }))].map(({ id, label }) => (
            <button key={id} onClick={() => setFilterAgent(id)}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer', background: filterAgent === id ? '#1B4B8A' : '#f3f4f6', color: filterAgent === id ? '#fff' : '#374151', transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #1B4B8A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, background: '#f0fdf4', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle size={32} color="#34d399" />
            </div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>Queue is clear</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#9ca3af' }}>{filterAgent ? `No pending assets from ${filterAgent}` : 'No pending assets from Wizkind agents'}</p>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {/* Info banner */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <Bot size={16} color="#1B4B8A" style={{ flexShrink: 0, marginTop: 1 }} />
            <p style={{ margin: 0, fontSize: 13, color: '#1e40af', lineHeight: 1.6 }}>
              These assets were created by Wizkind agents and are pending your review.{' '}
              <strong>Approve</strong> to add them to the live registry.{' '}
              <strong>Reject</strong> to archive them — they won't appear in the Asset Library.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {filtered.map(asset => (
              <div key={asset.id} style={{ transition: 'all 0.5s', opacity: justActioned?.id === asset.id ? 0 : 1, transform: justActioned?.id === asset.id ? 'scale(0.95)' : 'scale(1)' }}>
                <QueueCard asset={asset} onApprove={handleApprove} onArchive={handleArchive} processing={processing === asset.id} />
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
